// src/services/penaltyService.js
import { configuredAxios as api } from './axiosConfig';

const penaltyService = {
  // =========================================================================
  // 🔴 PUNIÇÕES GLOBAIS (ADMIN / TRIBUNAL)
  // =========================================================================

  // Punição global de jogador (Suspensão/Bloqueio)
  applyPlayerPenalty: async (data) => {
    try {
      const payload = {
        playerId: data.playerId || data.id,
        reason: data.reason,
        expiryDate: data.expiryDate,
      };
      const response = await api.post('/penalties/admin/penalty/player', payload);
      return response.data;
    } catch (error) {
      console.error('Erro ao aplicar punição de jogador:', error);
      throw error;
    }
  },

  // Punição global de time (Suspensão/Bloqueio)
  applyTeamPenalty: async (data) => {
    try {
      const payload = {
        teamId: data.teamId || data.id,
        reason: data.reason,
        expiryDate: data.expiryDate,
        banRoster: data.banRoster,
      };
      const response = await api.post('/penalties/admin/penalty/team', payload);
      return response.data;
    } catch (error) {
      console.error('Erro ao aplicar punição de time:', error);
      throw error;
    }
  },

  // Remover punição global de jogador ou time
  removePenalty: async (type, id) => {
    try {
      const response = await api.delete(`/penalties/admin/penalty/${type}/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao remover punição (${type}):`, error);
      throw error;
    }
  },

  // =========================================================================
  // 🔴 PERDA DE PONTOS (Compatível com 1 ou 2 argumentos)
  // =========================================================================
  applyPointDeduction: async (arg1, arg2) => {
    try {
      let championshipId;
      let data;

      // Detecta se chamou: applyPointDeduction(data) OU applyPointDeduction(championshipId, data)
      if (typeof arg1 === 'object' && arg1 !== null) {
        data = arg1;
        championshipId = data.championshipId || data.championship;
      } else {
        championshipId = arg1;
        data = arg2 || {};
      }

      const teamId = data.teamId || data.id || data.team;
      const points = parseInt(data.points, 10);
      const reason = data.reason || 'Perda de pontos por decisão do tribunal';

      // Validações defensivas
      if (!championshipId) {
        throw new Error('ID do campeonato não foi informado.');
      }
      if (!teamId) {
        throw new Error('ID do time (teamId) não foi informado.');
      }
      if (!points || isNaN(points) || points <= 0) {
        throw new Error('Informe uma quantidade válida de pontos (mínimo 1).');
      }

      const payload = {
        teamId,
        points,
        reason,
        source: data.source || 'tribunal',
      };

      const response = await api.post(`/championships/${championshipId}/point-deduction`, payload);
      return response.data;
    } catch (error) {
      console.error('Erro ao aplicar perda de pontos no penaltyService:', error);
      throw error;
    }
  },

  // Remover Perda de Pontos
  removePointDeduction: async (championshipId, deductionId) => {
    try {
      if (!championshipId || !deductionId) {
        throw new Error('ID do campeonato e ID da punição são obrigatórios.');
      }
      const response = await api.delete(`/championships/${championshipId}/point-deduction/${deductionId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao remover perda de pontos no penaltyService:', error);
      throw error;
    }
  },

  // =========================================================================
  // 🟢 CONSULTAS DE DEDUÇÕES DE PONTOS
  // =========================================================================

  // Buscar todas as deduções de pontos de um campeonato
  getPointDeductions: async (championshipId) => {
    try {
      if (!championshipId) {
        throw new Error('ID do campeonato é obrigatório.');
      }
      const response = await api.get(`/championships/${championshipId}/point-deductions`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar deduções de pontos:', error);
      throw error;
    }
  },

  // Buscar deduções de pontos de um time específico no campeonato
  getTeamPointDeductions: async (championshipId, teamId) => {
    try {
      if (!championshipId || !teamId) {
        throw new Error('ID do campeonato e ID do time são obrigatórios.');
      }
      const response = await api.get(`/championships/${championshipId}/teams/${teamId}/point-deductions`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar deduções de pontos do time:', error);
      throw error;
    }
  },

  // =========================================================================
  // 🟢 HISTÓRICO E ESTATÍSTICAS
  // =========================================================================

  // Buscar histórico completo de punições de um campeonato
  getPenaltyHistory: async (championshipId) => {
    try {
      if (!championshipId) {
        throw new Error('ID do campeonato é obrigatório.');
      }
      const response = await api.get(`/penalties/admin/history/${championshipId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar histórico de punições:', error);
      throw error;
    }
  },

  // Buscar estatísticas de punições de um campeonato
  getPenaltyStats: async (championshipId) => {
    try {
      if (!championshipId) {
        throw new Error('ID do campeonato é obrigatório.');
      }
      const response = await api.get(`/penalties/admin/stats/${championshipId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar estatísticas de punições:', error);
      throw error;
    }
  },

  // =========================================================================
  // 💰 MULTAS FINANCEIRAS POR CARTÕES & PAGAMENTOS PIX
  // =========================================================================

  // Buscar multas por campeonato (com filtro opcional por time)
  getFinesByChampionship: async (championshipId, teamId) => {
    try {
      if (!championshipId) {
        throw new Error('ID do campeonato é obrigatório.');
      }
      const response = await api.get(`/fines/championship/${championshipId}`, {
        params: teamId ? { teamId } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar multas:', error);
      throw error;
    }
  },

  // Gerar cobrança Pix dinâmica para pagamento da multa
  generateFinePayment: async (fineId) => {
    try {
      if (!fineId) {
        throw new Error('ID da multa é obrigatório.');
      }
      const response = await api.post(`/fines/${fineId}/generate-payment`);
      return response.data;
    } catch (error) {
      console.error('Erro ao gerar pagamento da multa:', error);
      throw error;
    }
  },
};

export default penaltyService;